import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { AI_AGENT } from "@/store/zustand/aiActionStore";
import { useCanvasId } from "@/utils/utils";
import { createComponentPrompt } from "./createComponentPrompt";

interface CreateComponentModalProps {
  isOpen: boolean;
  onClose: () => void;
  elementData?: {
    codebaseId: string;
    filename: string;
    jsxName?: string;
    contentsStart?: number;
    contentsEnd?: number;
  };
}

/**
 * Converts user input to a valid React component name
 * - Removes special characters
 * - Converts to PascalCase
 * - Replaces spaces with nothing
 * - Ensures it starts with uppercase letter
 */
export const convertToComponentName = (input: string): string => {
  const trimmedInput = input.trim();

  if (!trimmedInput) {
    return "";
  }

  // Remove special characters and split by spaces/underscores/hyphens
  const words = trimmedInput
    .replace(/[^a-zA-Z0-9\s_-]/g, "") // Remove special chars except spaces, underscores, hyphens
    .split(/[\s_-]+/) // Split by spaces, underscores, hyphens
    .filter((word) => word.length > 0);

  // Capitalize first letter of each word, but preserve the rest
  const pascalCaseWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1),
  );

  const result = pascalCaseWords.join("");

  // Ensure it starts with uppercase letter (should already be the case)
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const CreateComponentModal: React.FC<CreateComponentModalProps> = ({
  isOpen,
  onClose,
  elementData,
}) => {
  const [userInput, setUserInput] = useState("");
  const [context, setContext] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setSendChatMessage } = useAiContextStore();
  const { setNewLayout, expandLeft } = useLayoutStore();
  const canvasId = useCanvasId();

  // Convert user input to valid component name
  const componentName = convertToComponentName(userInput);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setUserInput("");
      setContext("");
      setError(null);
    }
  }, [isOpen]);

  const handleCreate = async () => {
    if (isCreating) return;

    if (!componentName.trim()) {
      setError("Component name is required");
      return;
    }

    if (!elementData) {
      setError("No element data available for component creation");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      if (!setSendChatMessage) {
        throw new Error("AI chat integration is not available");
      }

      // Generate AI prompt and instructions using extracted utility
      const promptData = createComponentPrompt({
        componentName,
        context,
        elementData,
        canvasId,
      });

      const success = setSendChatMessage({
        sendChatMessage: promptData.prompt,
        refreshAfterResponse: true,
        additionalInstructions: promptData.additionalInstructions,
        criticalFiles: promptData.criticalFiles,
        agentChoice: promptData.agentChoice as AI_AGENT,
        isFreePrompt: promptData.isFreePrompt,
        resetToggles: promptData.resetToggles,
      });

      if (!success) {
        throw new Error("Failed to send message - AI is busy");
      }

      // Switch to AI Chat tab and expand left panel
      setNewLayout(LeftTab.AIChat);
      expandLeft();

      // Close the modal after successfully sending
      handleCancel();
    } catch (error) {
      console.error("Error creating React component:", error);
      setError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while creating the component",
      );
      setIsCreating(false);
      return;
    }

    setIsCreating(false);
  };

  const handleCancel = () => {
    setUserInput("");
    setContext("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-[500px] bg-secondary-100 border-secondary-400 p-4 gap-2">
        <DialogHeader>
          <DialogTitle className="text-base font-normal text-white">
            New component
          </DialogTitle>
          <DialogDescription className="text-secondary-n-400 text-sm">
            Create a new React component from the selected element
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 space-y-4">
          {/* Component Name Field */}
          <div>
            <label className="block text-sm text-secondary-n-400 mb-2">
              Name
            </label>
            <input
              data-testid="create-component-name-input"
              type="text"
              className="w-full p-2 rounded-md bg-secondary-200 text-white text-sm placeholder-secondary-600 focus:outline-hidden focus:ring-1 transition-colors border-secondary-400 focus:border-primary-400 focus:ring-primary-400"
              placeholder="e.g., test button, my-component"
              value={userInput}
              onChange={(e) => {
                setUserInput(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (componentName.trim()) {
                    handleCreate();
                  }
                }
              }}
            />
            {/* Show converted component name */}
            {componentName && (
              <div className="text-primary-400 text-xs mt-1">
                Component{" "}
                <span className="font-mono">
                  {componentName} will be created
                </span>
              </div>
            )}
          </div>

          {/* Additional Context Field */}
          <div>
            <label className="block text-sm text-secondary-n-400 mb-2">
              Additional context (optional)
            </label>
            <textarea
              data-testid="create-component-context-input"
              className="w-full p-2 rounded-md bg-secondary-200 border border-secondary-400 text-white text-sm placeholder-secondary-600 focus:outline-hidden focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
              placeholder="Add additional context for AI"
              rows={3}
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />
          </div>

          {error && (
            <div className="text-red-400 text-xs bg-red-900/20 border border-red-800 rounded-md p-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isCreating}
              className="text-white hover:bg-secondary-300 text-sm rounded-md"
            >
              Cancel
            </Button>
            <Button
              data-testid="create-component-submit-button"
              variant="primary"
              size="sm"
              onClick={handleCreate}
              disabled={isCreating || !componentName.trim()}
              className="text-sm px-6 rounded-md"
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
